import React, { useCallback, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import {
     MONTH_PROBLEM_MESSAGE,
     monthDisplayName,
     monthFromSheetName,
     normaliseMonth,
} from "../../cxAwardMonth";

// The grid takes a height of its own so the table scrolls in both directions with its header
// pinned, instead of the page growing until the horizontal bar is somewhere off the bottom.
const GRID_MAX_HEIGHT = "58vh";

const MONTH_SHORT_NAMES = [
     "Jan", "Feb", "Mar", "Apr", "May", "Jun",
     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// The headings Renault writes, and the ones the New Car Sales template uses, because the file has
// arrived as both. The dealer code is matched to our records on upload, and everything stays
// visible before anything is sent.
const COLUMN_ALIASES = {
     dealerCode: ["retailer code", "dealer code", "dealercode", "retailercode"],
     dealerName: ["site", "dealer name", "dealername"],
     role: ["role"],
     firstName: ["first name", "firstname", "winner name"],
     lastName: ["last name", "lastname"],
     email: ["email", "e-mail"],
     reward: ["reward"],
     month: ["week", "month"],
     area: ["aftersales area", "sales area", "area"],
     awardSubject: ["type of award", "award type", "award for"],
};

// A row is the header when it names the two columns nothing can be loaded without. Renault's sheet
// carries one header per survey section rather than one for the sheet, so this is asked of every
// row rather than assumed of the first.
const REQUIRED_HEADER_FIELDS = ["email", "dealerCode"];

// The header on the real sheet is written "Week (1,2,3 etc)", so a header is a match when it starts
// with the alias rather than equalling it.
const headerMatches = (header, field) => {
     const key = header.trim().toLowerCase();

     return COLUMN_ALIASES[field].some(
          (alias) => key === alias || key.startsWith(alias),
     );
};

const readValue = (value) => {
     if (value === null || value === undefined) {
          return "";
     }

     // A month written "Aug 2026" is read as a date by the spreadsheet, and its raw value is a
     // serial number like 46235, which no reader of this screen could recognise as August. Turn it
     // back into the month and year it was typed as.
     if (value instanceof Date && !Number.isNaN(value.getTime())) {
          return `${MONTH_SHORT_NAMES[value.getMonth()]} ${value.getFullYear()}`;
     }

     return String(value).trim();
};

/**
 * The column each field sits in, or null when the row is not a header. Read once per header row and
 * used for every row beneath it, so a sheet holding several tables is read as several tables.
 */
const readHeaderColumns = (cells) => {
     const columns = {};

     cells.forEach((cell, index) => {
          const heading = readValue(cell).toLowerCase();

          if (!heading) {
               return;
          }

          Object.keys(COLUMN_ALIASES).forEach((field) => {
               if (columns[field] !== undefined) {
                    return;
               }

               if (headerMatches(heading, field)) {
                    columns[field] = index;
               }
          });
     });

     const isHeader = REQUIRED_HEADER_FIELDS.every(
          (field) => columns[field] !== undefined,
     );

     return isHeader ? columns : null;
};

/**
 * The reward in pounds. Renault writes it as "GBP 50 voucher" rather than a number, so the figure is
 * taken out of the text. A cell with no figure in it gives null, which the row reports as a problem
 * rather than paying nothing.
 */
const readReward = (value) => {
     const text = readValue(value);

     if (!text) {
          return null;
     }

     const figure = text.replace(/[^\d.]/g, "");

     return figure === "" ? null : Number(figure);
};

// Read from the row as it stands now, not as it arrived, so correcting a cell clears its problem
// and a corrected row becomes loadable without re-reading the file.
const rowProblem = (row) => {
     const problems = [];

     if (!row.dealer) {
          problems.push(
               `Dealer ${row.sheetDealerCode || "(blank)"} is not one we hold.`,
          );
     }

     if (!row.jobRole) {
          problems.push("Pick the role a new account is given.");
     }

     if (!String(row.email || "").trim()) {
          problems.push("Email is missing.");
     }

     if (!String(row.firstName || "").trim()) {
          problems.push("First name is missing.");
     }

     if (!String(row.lastName || "").trim()) {
          problems.push("Last name is missing.");
     }

     if (!normaliseMonth(row.month)) {
          problems.push(MONTH_PROBLEM_MESSAGE);
     }

     if (
          row.rewardValueGbp === null ||
          row.rewardValueGbp === undefined ||
          Number.isNaN(Number(row.rewardValueGbp)) ||
          Number(row.rewardValueGbp) < 0
     ) {
          problems.push("Reward must be a value in pounds.");
     }

     return problems.join(" ");
};

const CxAddWinnersImport = ({
     dealerOptions,
     jobRoleOptions,
     isSubmitting,
     onSubmit,
}) => {
     const [rows, setRows] = useState([]);
     const [readError, setReadError] = useState("");
     const [fileName, setFileName] = useState("");

     // The workbook is held so a different sheet can be opened without re-reading the file. Renault
     // sends several months in one book, one month per sheet.
     const [workbook, setWorkbook] = useState(null);
     const [sheetNames, setSheetNames] = useState([]);
     const [sheetName, setSheetName] = useState("");

     // The month every row on this batch is paid for. Nothing in the file carries it, so it is taken
     // from the sheet name and shown here to be corrected before anything is sent.
     const [batchMonth, setBatchMonth] = useState("");

     const setRowField = useCallback((rowIndex, field, value) => {
          setRows((previous) =>
               previous.map((row, index) =>
                    index === rowIndex ? { ...row, [field]: value } : row,
               ),
          );
     }, []);

     // The month belongs to the batch, not the row, because the file names it once per sheet. It is
     // still written onto every row so the grid shows what each winner will be paid for, and a row
     // that needs a different month can still be corrected on its own afterwards.
     const setMonthOnEveryRow = useCallback((month) => {
          setBatchMonth(month);
          setRows((previous) => previous.map((row) => ({ ...row, month })));
     }, []);

     const removeRow = useCallback((rowIndex) => {
          setRows((previous) =>
               previous.filter((row, index) => index !== rowIndex),
          );
     }, []);

     // An added row starts completely empty. Nothing is carried down from the row above, because a
     // value that arrived on its own is a value nobody checked.
     const addRow = useCallback(() => {
          setRows((previous) => [
               ...previous,
               {
                    firstName: "",
                    lastName: "",
                    email: "",
                    dealer: null,
                    jobRole: null,
                    month: "",
                    rewardValueGbp: null,
                    sheetDealerCode: "",
               },
          ]);
     }, []);

     const buildRow = (cells, columns, month, awardSubject) => {
          const readColumn = (field) => {
               const index = columns[field];

               if (index === undefined) {
                    return "";
               }

               return readValue(cells[index]);
          };

          const sheetDealerCode = readColumn("dealerCode");

          // Renault writes the winner's job title in the Role column, and Equipe now holds a role of
          // each of those names, so the title chooses the role. A title we hold no role for leaves
          // the row without one, which it reports rather than guessing.
          const sheetRole = readColumn("role").toLowerCase();
          const jobRole = jobRoleOptions?.find(
               (option) => String(option.name).trim().toLowerCase() === sheetRole,
          );

          // The dealer is resolved against the whole lookup, never a capped list. A capped lookup is
          // what left 23 of 27 rows on the retail import reading "Dealer not set".
          const dealer = dealerOptions.find(
               (option) => String(option.dealerCode).trim() === sheetDealerCode,
          );

          return {
               firstName: readColumn("firstName"),
               lastName: readColumn("lastName"),
               email: readColumn("email"),
               dealer: dealer || null,
               jobRole: jobRole || null,
               month,
               // The award subject arrives either as a column of its own or as the section title
               // above the block. A column wins, because it is written per winner.
               awardSubject: readColumn("awardSubject") || awardSubject || "",
               rewardValueGbp: readReward(readColumn("reward")),
               sheetDealerCode,
          };
     };

     // Reads one sheet of the workbook. Renault's sheet is not one table: it opens with a section
     // title, then a header row, then its winners, then a second title and header for the service
     // survey. So the header is found wherever it is and the rows under it are read until the next
     // header appears, rather than assuming the file starts with a header on row one.
     const readSheet = useCallback(
          (workbook, sheetName) => {
               const sheet = workbook.Sheets[sheetName];
               const grid = XLSX.utils.sheet_to_json(sheet, {
                    header: 1,
                    defval: "",
                    blankrows: false,
               });

               const month = monthFromSheetName(sheetName);

               let columns = null;
               let awardSubject = "";
               const parsed = [];

               grid.forEach((cells) => {
                    const headerColumns = readHeaderColumns(cells);

                    if (headerColumns) {
                         columns = headerColumns;
                         return;
                    }

                    // A section title is one filled cell on its own line. It carries no winner, it
                    // names what the award was for, and it holds until the next title replaces it.
                    const filled = cells.filter(
                         (cell) => String(cell ?? "").trim() !== "",
                    );

                    if (filled.length <= 1) {
                         if (filled.length === 1) {
                              awardSubject = String(filled[0]).trim();
                         }

                         return;
                    }

                    if (!columns) {
                         return;
                    }

                    parsed.push(buildRow(cells, columns, month, awardSubject));
               });

               return { rows: parsed, month };
          },
          [buildRow],
     );

     const openSheet = useCallback(
          (workbook, sheetName) => {
               const { rows: parsed, month } = readSheet(workbook, sheetName);

               setSheetName(sheetName);
               setBatchMonth(month);
               setRows(parsed);

               if (!parsed.length) {
                    setReadError(
                         "No winners were found on that sheet. Pick another sheet, or check the file has a header row naming the email and dealer columns.",
                    );
               } else {
                    setReadError("");
               }
          },
          [readSheet],
     );

     const readFile = (event) => {
          const file = event.target.files?.[0];

          setReadError("");
          setRows([]);
          setWorkbook(null);
          setSheetNames([]);
          setFileName(file ? file.name : "");

          if (!file) {
               return;
          }

          const reader = new FileReader();

          reader.onerror = () =>
               setReadError("That file could not be read. Try saving it again as .xlsx.");

          reader.onload = () => {
               try {
                    const book = XLSX.read(reader.result, { type: "array", cellDates: true });

                    if (!book.SheetNames.length) {
                         setReadError("That file has no sheets.");
                         return;
                    }

                    setWorkbook(book);
                    setSheetNames(book.SheetNames);

                    // A month-per-sheet workbook opens on the first sheet that names a month, so a
                    // three month file does not silently load whichever tab happens to be first.
                    const named = book.SheetNames.find((name) => monthFromSheetName(name));

                    openSheet(book, named || book.SheetNames[0]);
               } catch (error) {
                    setReadError(
                         "That file could not be read as a spreadsheet. Try saving it again as .xlsx.",
                    );
               }
          };

          reader.readAsArrayBuffer(file);
     };

     // The row number and the problem are computed from the rows rather than stored, so neither can
     // disagree with what is on screen after an edit.
     const gridRows = useMemo(
          () =>
               rows.map((row, index) => ({
                    ...row,
                    rowIndex: index,
                    rowNumber: index + 1,
                    problem: rowProblem(row),
               })),
          [rows],
     );

     const loadable = useMemo(
          () => gridRows.filter((row) => !row.problem),
          [gridRows],
     );

     // Built once. Rebuilding a cell on every render hands the input a new identity per keystroke,
     // which loses the caret.
     const cells = useMemo(() => {
          const textCell = (field) => (cellProps) => (
               <td>
                    <Input
                         style={{ width: "100%" }}
                         value={cellProps.dataItem[field] || ""}
                         onChange={(event) =>
                              setRowField(
                                   cellProps.dataItem.rowIndex,
                                   field,
                                   event.value,
                              )
                         }
                    />
               </td>
          );

          return {
               firstName: textCell("firstName"),
               lastName: textCell("lastName"),
               email: textCell("email"),
               month: textCell("month"),
               awardSubject: textCell("awardSubject"),

               dealer: (cellProps) => (
                    <td>
                         <DropDownList
                              style={{ width: "100%" }}
                              data={dealerOptions}
                              textField="label"
                              dataItemKey="id"
                              value={cellProps.dataItem.dealer}
                              onChange={(event) =>
                                   setRowField(
                                        cellProps.dataItem.rowIndex,
                                        "dealer",
                                        event.value,
                                   )
                              }
                         />
                    </td>
               ),

               jobRole: (cellProps) => (
                    <td>
                         <DropDownList
                              style={{ width: "100%" }}
                              data={jobRoleOptions}
                              textField="name"
                              dataItemKey="id"
                              value={cellProps.dataItem.jobRole}
                              onChange={(event) =>
                                   setRowField(
                                        cellProps.dataItem.rowIndex,
                                        "jobRole",
                                        event.value,
                                   )
                              }
                         />
                    </td>
               ),

               rewardValueGbp: (cellProps) => (
                    <td>
                         <NumericTextBox
                              style={{ width: "100%" }}
                              min={0}
                              step={5}
                              value={cellProps.dataItem.rewardValueGbp}
                              onChange={(event) =>
                                   setRowField(
                                        cellProps.dataItem.rowIndex,
                                        "rewardValueGbp",
                                        event.value,
                                   )
                              }
                         />
                    </td>
               ),

               remove: (cellProps) => (
                    <td>
                         <Tooltip title="Remove row">
                              <IconButton
                                   aria-label="Remove row"
                                   onClick={() =>
                                        removeRow(cellProps.dataItem.rowIndex)
                                   }
                              >
                                   <DeleteIcon color="error" />
                              </IconButton>
                         </Tooltip>
                    </td>
               ),
          };
     }, [dealerOptions, jobRoleOptions, removeRow, setRowField]);

     // A row that still has a problem is tinted whole, so it reads as one thing to fix rather than a
     // sentence to hunt for at the end of a wide table.
     const rowRender = (element, rowProps) => {
          if (!rowProps.dataItem.problem) {
               return element;
          }

          return React.cloneElement(
               element,
               { ...element.props, style: { backgroundColor: "#fdecea" } },
               element.props.children,
          );
     };

     const submit = () => {
          onSubmit(
               loadable.map((row) => ({
                    dealershipId: row.dealer.id,
                    jobRoleId: row.jobRole.id,
                    email: row.email.trim(),
                    firstName: row.firstName.trim(),
                    lastName: row.lastName.trim(),
                    rewardValueGbp: Number(row.rewardValueGbp),
                    month: normaliseMonth(row.month),
                    awardSubject: String(row.awardSubject ?? "").trim(),
               })),
          );
     };

     return (
          <Box>
               <Typography variant="body2" sx={{ mb: 2 }}>
                    The file is Renault's monthly sheet. The Week column carries the
                    month, and the Reward column is in pounds. Every row can be
                    corrected here before anything is sent, and a winner already paid
                    for that month is reported back rather than paid twice.
               </Typography>

               <input type="file" accept=".xlsx,.xls,.csv" onChange={readFile} />

               {sheetNames.length > 0 && (
                    <Box
                         sx={{
                              pt: 2,
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-end",
                              gap: 2,
                         }}
                    >
                         <DropDownList
                              style={{ width: "280px" }}
                              label="Sheet"
                              data={sheetNames}
                              value={sheetName}
                              onChange={(event) =>
                                   openSheet(workbook, event.value)
                              }
                         />
                         <Input
                              style={{ width: "200px" }}
                              label="Month these are paid for"
                              value={batchMonth}
                              onChange={(event) => setMonthOnEveryRow(event.value)}
                         />
                         <Typography variant="body2" sx={{ pb: 1 }}>
                              {monthDisplayName(batchMonth)
                                   ? `Every row on this sheet is paid for ${monthDisplayName(batchMonth)}.`
                                   : "Name the month before loading, the file does not carry one."}
                         </Typography>
                    </Box>
               )}

               {fileName && (
                    <Typography variant="caption" sx={{ display: "block", pt: 1 }}>
                         {fileName}
                    </Typography>
               )}

               {readError && (
                    <Typography variant="body2" color="error" sx={{ pt: 1 }}>
                         {readError}
                    </Typography>
               )}

               <Box sx={{ pt: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                         {loadable.length} of {gridRows.length} rows can be loaded.
                    </Typography>

                    <Grid
                         data={gridRows}
                         className="sba-area-data-grid"
                         rowRender={rowRender}
                         style={{ maxHeight: GRID_MAX_HEIGHT }}
                    >
                         <GridColumn field="rowNumber" title="Row" width="70px" />
                         <GridColumn
                              field="firstName"
                              title="First"
                              width="150px"
                              cells={{ data: cells.firstName }}
                         />
                         <GridColumn
                              field="lastName"
                              title="Last"
                              width="150px"
                              cells={{ data: cells.lastName }}
                         />
                         <GridColumn
                              field="email"
                              title="Email"
                              width="260px"
                              cells={{ data: cells.email }}
                         />
                         <GridColumn
                              field="dealer"
                              title="Dealer"
                              width="260px"
                              cells={{ data: cells.dealer }}
                         />
                         <GridColumn
                              field="jobRole"
                              title="Role for a new account"
                              width="200px"
                              cells={{ data: cells.jobRole }}
                         />
                         {/* The job title Renault typed is read from the file and printed on the
                             activation report, but it is not shown here. It names nothing the
                             upload decides, and the role that matters is the column beside it. */}
                         <GridColumn
                              field="month"
                              title="Month"
                              width="140px"
                              cells={{ data: cells.month }}
                         />
                         <GridColumn
                              field="awardSubject"
                              title="Award for"
                              width="200px"
                              cells={{ data: cells.awardSubject }}
                         />
                         <GridColumn
                              field="rewardValueGbp"
                              title="Reward (GBP)"
                              width="140px"
                              cells={{ data: cells.rewardValueGbp }}
                         />
                         <GridColumn
                              title=""
                              width="70px"
                              cells={{ data: cells.remove }}
                         />
                         <GridColumn field="problem" title="Problem" width="380px" />
                    </Grid>

                    <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                         <Tooltip title="Add a row">
                              <IconButton
                                   aria-label="Add a row"
                                   onClick={addRow}
                                   sx={{
                                        backgroundColor: "#1976d2",
                                        color: "#fff",
                                        "&:hover": { backgroundColor: "#1565c0" },
                                   }}
                              >
                                   <AddIcon />
                              </IconButton>
                         </Tooltip>
                    </Box>

                    <Box sx={{ pt: 3 }}>
                         <Button
                              type="button"
                              themeColor="primary"
                              disabled={isSubmitting || !loadable.length}
                              onClick={submit}
                         >
                              {isSubmitting
                                   ? "Adding..."
                                   : `Add ${loadable.length} winners`}
                         </Button>
                    </Box>
               </Box>
          </Box>
     );
};

export default CxAddWinnersImport;
